// @flow
import i18n from 'i18next';
import * as Sentry from '@sentry/browser';
import type { Dispatch, GetState } from 'statsradio/types/Store';
import UserAPI from 'common/services/UserAPI';
import RadioUser from 'statsradio/domain/RadioUser';
import Radio from 'common/domain/radio';
import { switchLanguage } from 'common/i18n/i18n';
import {
  closeNotificationClient,
  openNotificationClient,
} from 'statsradio/modules/notifications/actions/notificationActions';
import ApiClient from 'common/services/api';
import { getStation, getToken, getUser } from '../reducers/sessionReducer';
import SessionAPI from '../services/SessionAPI';
import SessionStorage from '../services/SessionStorage';

export const BEGIN_SESSION = 'session/BEGIN_SESSION';
export const CLOSE_SESSION = 'session/CLOSE_SESSION';
export const RETRIEVE_NO_SESSION = 'session/RETRIEVE_NO_SESSION';
export const SESSION_FETCHED = 'session/SESSION_FETCHED';

export const SWITCH_RADIO = 'session/SWITCH_RADIO';
export const SWITCH_RADIO_SUCCESS = 'session/SWITCH_RADIO_SUCCESS';
export const SWITCH_RADIO_FAILED = 'session/SWITCH_RADIO_FAILED';

export const RENEW_SESSION = 'session/RENEW_SESSION';
export const RENEW_SESSION_SUCCESS = 'session/RENEW_SESSION_SUCCESS';
export const RENEW_SESSION_FAILED = 'session/RENEW_SESSION_FAILED';

export const UPDATE_RADIOUSER = 'session/UPDATE_RADIOUSER';

export const beginSession = (registration: ?{ token: ?string, profile: ?Object }) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const session = SessionStorage.retrieveNotExpired();
    if (!session) {
      dispatch({ type: RETRIEVE_NO_SESSION });
      return;
    }

    if (registration && registration.token && registration.profile) {
      dispatch({ type: BEGIN_SESSION, user: registration.profile, token: registration.token });
      const token = registration.token || '';
      ApiClient.setAuthorization(token);
      dispatch({ type: SESSION_FETCHED });
      return;
    }

    try {
      const { token, refreshToken } = await SessionAPI.renewSession(session.refreshToken);
      const user = await UserAPI.fetchGeneralUserInformation(token);
      dispatch(openNotificationClient());

      if (user.language !== i18n.language) {
        switchLanguage(() => {
        });
      }

      dispatch({ type: BEGIN_SESSION, user: user, token: token });
      ApiClient.setAuthorization(token);
      SessionStorage.updateRefreshToken(refreshToken);
      SessionStorage.updateSentryUser(user);
      Sentry.setUser(SessionStorage.retrieveSentryUser());
      if (session.callSign) {
        const radio = user.findRadio(session.callSign);
        radio && await dispatch(switchRadio(radio));
      }

      dispatch({ type: SESSION_FETCHED });
    } catch (error) {
      dispatch({ type: RETRIEVE_NO_SESSION });
    }
  };
};

export function closeSession() {
  return async (dispatch: Dispatch, getState: GetState) => {
    SessionStorage.clear();
    ApiClient.removeAuthorization();

    dispatch(closeNotificationClient());
    dispatch({ type: CLOSE_SESSION });
  };
}

export const renewSession = (): Function => {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({ type: RENEW_SESSION });

    const session = SessionStorage.retrieveNotExpired();
    if (!session) {
      dispatch({ type: RENEW_SESSION_FAILED });
      return;
    }

    const { refreshToken } = session;
    try {
      const newSession = await SessionAPI.renewSession(refreshToken);

      dispatch({ type: RENEW_SESSION_SUCCESS, token: newSession.token });
      ApiClient.setAuthorization(newSession.token);
      SessionStorage.updateRefreshToken(newSession.refreshToken);
      Sentry.setUser(SessionStorage.retrieveSentryUser());
    } catch (error) {
      dispatch({ type: RENEW_SESSION_FAILED });
      SessionStorage.clear();
    }
  };
};

export const switchRadio = (radio: Radio): Function => {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({ type: SWITCH_RADIO });

    const token = getToken(getState());

    try {
      let data = await UserAPI.fetchUserInformationForStation(token, radio.callSign);
      data = {
        ...data,
      };
      const radioUser = RadioUser.parse(data);

      dispatch({ type: SWITCH_RADIO_SUCCESS, radio: radio, radioUser: radioUser });
      SessionStorage.updateCurrentRadio(radio);
    } catch (error) {
      dispatch({ type: SWITCH_RADIO_FAILED, error: error });
    }
  };
};

export const switchRadioByCallSign = (callSign: string): Function => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const user = getUser(getState());
    const currentCallSign = getStation(getState());

    if (currentCallSign === callSign) {
      return;
    }

    const radio = user && user.radios.find(r => r.callSign === callSign);
    const definedRadio: Radio = (radio: any);

    if (radio) {
      await dispatch(switchRadio(definedRadio));
    } else {
      dispatch({ type: SWITCH_RADIO_FAILED, error: new Error('Radio not found') });
    }
  };
};

export const updateRadioUser = (radioUser: RadioUser) => {
  return { type: UPDATE_RADIOUSER, radioUser: radioUser };
};



// WEBPACK FOOTER //
// ./src/statsradio/modules/authentication/session/actions/sessionActions.js